import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Chip, PageHead, Panel, StatCard } from "@guestpilot/ui-kit";
import {
  Alert20Regular,
  CalendarClock20Regular,
  People20Regular,
  PeopleTeam20Regular,
} from "@fluentui/react-icons";
import { useGet } from "../api/hooks";
import type { Schema } from "../api/types";
import { RISK_ORDER, TeamsTable } from "../components/TeamsTable";
import { formatDate } from "../format";
import { InboxPreview } from "../components/InboxPreview";
import { useI18n } from "../i18n";
import { useSession } from "../session";

type Dashboard = Schema<"Dashboard">;

/** The admin's daily landing page: what needs attention across every controlled Team (BR-007, BR-222). */
export function Home() {
  const { t, locale } = useI18n();
  const s = useSession();
  const nav = useNavigate();
  const q = useGet<Dashboard>("/api/v1/dashboard");
  const [risk, setRisk] = useState<string | null>(null);
  const d = q.data;
  const tz = s.me.tenant.timeZone;
  const chips = RISK_ORDER.filter((r) => (d?.riskChips?.[r] ?? 0) > 0);
  const teams = (d?.teams ?? []).filter((x) => !risk || (x.riskCounts?.[risk] ?? 0) > 0);

  return (
    <div className="gp-page">
      <PageHead title={t("ui:home.title")} subtitle={t("ui:home.subtitle", { org: s.me.tenant.name })} />
      <InboxPreview />
      <div className="gp-grid-4">
        <StatCard
          icon={<PeopleTeam20Regular />}
          label={t("ui:home.teams")}
          value={d ? `${d.controlledTeams ?? 0}` : "—"}
          caption={
            d?.teamLimit === -1
              ? t("ui:home.teamsUnlimited")
              : t("ui:home.teamsOf", { limit: d?.teamLimit ?? 0 })
          }
        />
        <StatCard
          icon={<People20Regular />}
          label={t("ui:home.users")}
          value={d ? `${d.externalUsers ?? 0}` : "—"}
          caption={t("ui:home.usersAcross", { n: d?.teamsWithUsers ?? 0 })}
        />
        <StatCard
          attention
          icon={<Alert20Regular />}
          label={t("ui:home.attention")}
          value={d ? `${d.needsAttention ?? 0}` : "—"}
          caption={t("ui:home.attentionCaption")}
        />
        {d?.trialDaysLeft !== null && d?.trialDaysLeft !== undefined ? (
          <StatCard
            attention
            icon={<CalendarClock20Regular />}
            label={t("ui:home.trial")}
            value={`${d.trialDaysLeft}`}
            caption={
              s.teams.mobile
                ? undefined
                : t("ui:home.trialCaption", { date: formatDate(s.me.tenant.trialEndsAt, locale, tz) })
            }
          />
        ) : (
          <StatCard
            icon={<CalendarClock20Regular />}
            label={t("ui:home.nextReview")}
            value={d?.nextReviewDue ? formatDate(d.nextReviewDue, locale, tz) : "—"}
          />
        )}
      </div>

      {chips.length > 0 ? (
        <section aria-labelledby="risk-h" className="gp-stack">
          <h2 id="risk-h" className="t-title-sm" style={{ margin: 0 }}>
            {t("ui:home.filterRisk")}
          </h2>
          <div className="gp-row">
            {chips.map((r) => (
              <Chip
                key={r}
                variant={
                  r === "expired" || r === "overdue_review" || r === "unknown_domain" ? "danger" : "warn"
                }
                pressed={risk === r}
                onClick={() => setRisk(risk === r ? null : r)}
              >
                {t(`ui:risk.${r}`)} {d?.riskChips?.[r]}
              </Chip>
            ))}
          </div>
        </section>
      ) : null}

      <section aria-labelledby="teams-h" className="gp-stack">
        <div className="gp-section-head">
          <h2 id="teams-h" className="t-title-sm" style={{ margin: 0 }}>
            {t("ui:home.controlled")}
          </h2>
          <Link to="/teams" className="t-body-strong">
            {t("ui:home.viewAll")}
          </Link>
        </div>
        <Panel label={t("ui:home.controlled")}>
          <TeamsTable
            rows={d ? teams.slice(0, 5) : undefined}
            loading={q.isLoading}
            error={q.isError}
            onRetry={() => void q.refetch()}
            tz={tz}
            emptyAction={
              s.can("TEAM_ACTIVATE") ? (
                <Button variant="primary" onClick={() => nav("/teams?activate=1")}>
                  {t("ui:onboarding.activate")}
                </Button>
              ) : undefined
            }
          />
        </Panel>
      </section>
    </div>
  );
}
